import React from 'react'
import { pipe, prop } from 'lodash/fp'
import { EllipsisVerticalIcon } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { useSettingsSchemasQuery } from '@/entities/core'

import { ActionsDropdown } from '@/shared/components/ActionsDropdown'
import { ErrorBoundary } from '@/shared/components/ErrorBoundary'
import { RHFSelect } from '@/shared/components/HookForm'
import { Blanket } from '@/shared/components/ui/blanket'
import { Button } from '@/shared/components/ui/button'
import {
	Card,
	CardAction,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from '@/shared/components/ui/card'
import { Loader } from '@/shared/components/ui/loader'
import {
	getEnumOptions,
	getResolvedSchema,
	translateOptions,
	translateSchemaKeys,
} from '@/shared/tools/json-schema-utils'
import { dotCase } from '@/shared/tools/utils'

export const StrategyFieldsCard = ({
	prefix,
	actions,
}: {
	prefix?: string
	actions: any[]
}) => {
	const { t } = useTranslation()

	const schemaQuery = useSettingsSchemasQuery({
		select: pipe([
			getResolvedSchema(`part:strategy`),
			(schema) => translateSchemaKeys(schema, t),
		]),
	})
	if (schemaQuery.isLoading) {
		return (
			<div className='flex size-full items-center justify-center'>
				<Loader />
			</div>
		)
	}

	return (
		<Card data-testid='og-settings-strategies-panel'>
			<CardHeader className='grid-rows-1'>
				<CardTitle>
					{t('settings.strategy.long', { defaultValue: 'Pricing strategy' })}
				</CardTitle>
				<CardAction>
					<ActionsDropdown actions={actions}>
						<Button
							data-testid='pc-actions-popover-trigger'
							variant='ghost'
							size='icon-sm'
						>
							<EllipsisVerticalIcon />
						</Button>
					</ActionsDropdown>
				</CardAction>
			</CardHeader>
			<CardContent className='grid gap-3'>
				<Blanket
					isTinted={schemaQuery.isLoading}
					className='absolute flex items-center justify-center rounded-lg bg-white/30 backdrop-blur-xs dark:bg-black/30'
				>
					<Loader />
				</Blanket>
				<ErrorBoundary>
					<RHFSelect
						name={dotCase([prefix, 'strategy', 'demand_strategy', 'target'])}
						className='w-56'
						label={prop(
							'properties.demand_strategy.properties.target.title',
							schemaQuery.data
						)}
						description={prop(
							'properties.demand_strategy.properties.target.description',
							schemaQuery.data
						)}
						options={translateOptions(
							getEnumOptions(
								prop(
									'properties.demand_strategy.properties.target',
									schemaQuery.data
								)
							),
							t
						)}
					/>
					<RHFSelect
						name={dotCase([prefix, 'strategy', 'demand_strategy', 'protect'])}
						className='w-56'
						label={prop(
							'properties.demand_strategy.properties.protect.title',
							schemaQuery.data
						)}
						description={prop(
							'properties.demand_strategy.properties.protect.description',
							schemaQuery.data
						)}
						options={translateOptions(
							getEnumOptions(
								prop(
									'properties.demand_strategy.properties.protect',
									schemaQuery.data
								)
							),
							t
						)}
					/>
				</ErrorBoundary>
			</CardContent>
			<CardHeader>
				<CardTitle>
					{t('optimization_groups_settings_tab_markdown_title')}
				</CardTitle>
				<CardDescription>
					{t('optimization_groups_settings_tab_markdown_subtitle')}
				</CardDescription>
			</CardHeader>
			<CardContent>
				<ErrorBoundary>
					<RHFSelect
						name={dotCase([prefix, 'strategy', 'markdown_strategy', 'target'])}
						className='w-56'
						label={prop(
							'properties.markdown_strategy.properties.target.title',
							schemaQuery.data
						)}
						description={prop(
							'properties.markdown_strategy.properties.target.description',
							schemaQuery.data
						)}
						options={translateOptions(
							getEnumOptions(
								prop(
									'properties.markdown_strategy.properties.target',
									schemaQuery.data
								)
							),
							t
						)}
					/>
				</ErrorBoundary>
			</CardContent>
		</Card>
	)
}
